import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Save, Target } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import auditSectionService from '../../../../../../services/audit_services/auditSectionService';
import auditPlanService from '../../../../../../services/audit_services/auditPlanService';

export default function AuditSectionEdit() {
  const { auditPlanId, id } = useParams();
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [auditPlan, setAuditPlan] = useState(null);
  const [formData, setFormData] = useState({
    section_name: '',
    location: '',
    audit_plan: auditPlanId || ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!auditPlanId) {
      showError('L\'ID du plan d\'audit est requis');
      navigate('/dashboard/audit/my-plans');
      return;
    }
    
    fetchAuditPlan();
    fetchAuditSectionData();
  }, [id, auditPlanId]);

  const fetchAuditPlan = async () => {
    try {
      const result = await auditPlanService.getMyAuditPlanDetail(authTokens.access, auditPlanId);
      if (result.success) {
        setAuditPlan(result.data);
      } else {
        showError(result.error || 'Erreur lors du chargement du plan d\'audit');
      }
    } catch (error) {
      console.error('Error fetching audit plan:', error);
      showError('Erreur lors du chargement du plan d\'audit');
    }
  };

  const fetchAuditSectionData = async () => {
    setInitialLoading(true);
    
    try {
      const result = await auditSectionService.getAuditSectionDetail(authTokens.access, auditPlanId, id);
      
      if (result.success) {
        setFormData({
          section_name: result.data.section_name,
          location: result.data.location,
          audit_plan: result.data.audit_plan?.id || auditPlanId
        });
      } else {
        showError(result.error || 'Erreur lors du chargement des données de la section d\'audit');
      }
    } catch (error) {
      console.error('Error fetching audit section data:', error);
      showError('Erreur lors du chargement des données de la section d\'audit');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.section_name.trim()) {
      newErrors.section_name = 'Le nom de la section est requis';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'La localisation est requise';
    }



    if (!formData.audit_plan) {
      newErrors.audit_plan = 'Le plan d\'audit est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Veuillez corriger les erreurs avant de soumettre');
      return;
    }

    setLoading(true);

    try {
      // Transform data to match backend expectations
      const backendData = {
        section_name: formData.section_name,
        location: formData.location,
        audit_plan_id: formData.audit_plan
      };

      const result = await auditSectionService.updateAuditSection(authTokens.access, auditPlanId, id, backendData);
      
      if (result.success) {
        showSuccess('Section d\'audit mise à jour avec succès');
        navigate(`/dashboard/audit/audit-plans/${auditPlanId}/sections/${id}`);
      } else {
        showError(result.error || 'Erreur lors de la mise à jour de la section d\'audit');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error updating audit section:', error);
      showError('Erreur lors de la mise à jour de la section d\'audit');
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données de la section...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to={-1}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Modifier la section d'audit</h1>
          <p className="text-sm text-gray-600 mt-1">Mettre à jour les informations et l'organisation de la section</p>
        </div>
      </div>

      {/* Audit Plan Info */}
      {auditPlan && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <Target className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-sm text-green-900">
              Modification de section pour :{' '}
              <Link
                to={`/dashboard/audit/my-plans/${auditPlan.id}`}
                className="font-semibold hover:underline"
              >
                {auditPlan.title}
              </Link>
            </span>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Informations de la section</h3>
          
          <div className="space-y-6">
            {/* Section Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom de la section <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.section_name}
                onChange={(e) => handleInputChange('section_name', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.section_name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="ex: Infrastructure IT, Sécurité des Données"
              />
              {errors.section_name && <p className="text-red-500 text-sm mt-1">{errors.section_name}</p>}
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Localisation <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.location ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="ex: Douala, Buea"
              />
              {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
            </div>

          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4">
          <Link
            to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${id}`}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Mise à jour en cours...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Mettre à jour la section
              </>
            )}
          </button>
        </div>
      </form>

    </div>
  );
}

